import { Fragment, isValidElement, useCallback, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";
import { kieli, lokaali, t, virheTeksti } from "./kieli";
import type { KaannosAvain } from "./kaannokset";
import { NimikirjainKentta, useNimikirjaimet } from "./Testauspalaute";

/* ═══ DEMOTESTAUKSEN OHJE (Toni 10.10.2026 «tää pitää toteuttaa») ═══
   Ohje on demon sisällä eikä sähköpostin PDF:nä: PDF vanhenee heti kun demo muuttuu. Kirjautunut näkee oman roolinsa
   ohjeen omalla kielellään (palvelin valitsee roolin tunnuksesta). Sisältö on repossa demo/testaus/<rooli>.<kieli>.md,
   ja kävely vertaa ohjeen alussa lueteltuja näkymiä roolin valikkoon. Tulostus samasta näkymästä (A4).
   Markdown piirretään tässä itse: otsikot, luettelot, kappaleet, lihavointi ja koodi — ei uutta riippuvuutta. */

function rivinSisalto(s: string): ReactNode[] {
  const osat: ReactNode[] = [];
  // Lihavointi **…**, kursiivi *…* tai _…_ (ei sanan sisällä: tunnus_osa_x jää ennalleen) ja koodi (T481, Tonin havainto:
  // «_Jos luku näyttää liian hyvältä…_» tulostui alaviivoineen).
  const re = /(\*\*[^*]+\*\*|`[^`]+`|(?<![\w*])\*[^*\s][^*]*\*(?![\w*])|(?<![\p{L}\p{N}_])_[^_\s][^_]*_(?![\p{L}\p{N}_]))/gu;
  let i = 0; let m: RegExpExecArray | null; let n = 0;
  while ((m = re.exec(s))) {
    if (m.index > i) osat.push(s.slice(i, m.index));
    const x = m[0];
    osat.push(x.startsWith("**") ? <strong key={n++}>{x.slice(2, -2)}</strong>
      : x.startsWith("`") ? <code key={n++}>{x.slice(1, -1)}</code> : <em key={n++}>{x.slice(1, -1)}</em>);
    i = m.index + x.length;
  }
  if (i < s.length) osat.push(s.slice(i));
  return osat;
}

/** T487: «Tee nämä» / «Do these» -osion numeroitu luettelo saa jokaisen kohdan alle `tehtava(nro, teksti)`-sisällön. */
const TEHTAVAOTSIKKO = /^(tee nämä|do these)\b/i;

export function Markdown({ teksti, tehtava }: { teksti: string; tehtava?: (nro: number, teksti: string) => ReactNode }) {
  const lohkot: ReactNode[] = [];
  let otsikko = "";
  const rivit = teksti.replace(/\r\n/g, "\n").split("\n");
  let lista: { tyyppi: "ul" | "ol"; rivit: string[] } | null = null;
  let kappale: string[] = [];
  const tyhjenna = () => {
    if (kappale.length) { lohkot.push(<p key={lohkot.length}>{rivinSisalto(kappale.join(" "))}</p>); kappale = []; }
    if (lista) {
      const L = lista.tyyppi;
      const tehtavat = tehtava && L === "ol" && TEHTAVAOTSIKKO.test(otsikko);
      const sisalto = lista.rivit.map((r, j) => tehtavat
        ? <li key={j} className="testaus__tehtava" data-testaus-tehtava={j + 1}><div>{rivinSisalto(r)}</div>{tehtava(j + 1, r)}</li>
        : <li key={j}>{rivinSisalto(r)}</li>);
      lohkot.push(L === "ul" ? <ul key={lohkot.length}>{sisalto}</ul> : <ol key={lohkot.length}>{sisalto}</ol>);
      lista = null;
    }
  };
  for (const r of rivit) {
    const h = /^(#{1,3})\s+(.*)$/.exec(r);
    const ul = /^\s*[-*]\s+(.*)$/.exec(r);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(r);
    if (h) {
      tyhjenna();
      const taso = h[1]!.length;
      otsikko = h[2]!;
      const sis = rivinSisalto(h[2]!);
      lohkot.push(taso === 1 ? <h1 key={lohkot.length}>{sis}</h1> : taso === 2 ? <h2 key={lohkot.length}>{sis}</h2> : <h3 key={lohkot.length}>{sis}</h3>);
    } else if (ul || ol) {
      const tyyppi = ul ? "ul" : "ol";
      if (kappale.length) { lohkot.push(<p key={lohkot.length}>{rivinSisalto(kappale.join(" "))}</p>); kappale = []; }
      if (lista && lista.tyyppi !== tyyppi) tyhjenna();
      if (!lista) lista = { tyyppi, rivit: [] };
      lista.rivit.push((ul ?? ol)![1]!);
    } else if (!r.trim()) {
      tyhjenna();
    } else {
      if (lista) tyhjenna();
      kappale.push(r.trim());
    }
  }
  tyhjenna();
  // Kaksi peräkkäistä listaosiota («Pitää näkyä» + «Ei saa näkyä») rinnakkain (T481, Toni 11.10.2026: testausohje yhdelle
  // A4-sivulle). Kieliriippumaton: tunnistus rakenteesta (h2 + pelkkä lista, kahdesti), ei otsikon tekstistä.
  const laji = (l: ReactNode) => (isValidElement(l) && typeof l.type === "string" ? l.type : "");
  const ulos: ReactNode[] = [];
  for (let i = 0; i < lohkot.length; i++) {
    if (laji(lohkot[i]) === "h2" && laji(lohkot[i + 1]) === "ul" && laji(lohkot[i + 2]) === "h2" && laji(lohkot[i + 3]) === "ul"
        && laji(lohkot[i + 4]) !== "ul") {
      ulos.push(<div key={i} className="md__parit" data-md-parit="">
        <section>{lohkot[i]}{lohkot[i + 1]}</section><section>{lohkot[i + 2]}{lohkot[i + 3]}</section></div>);
      i += 3;
    } else ulos.push(<Fragment key={i}>{lohkot[i]}</Fragment>);
  }
  return <>{ulos}</>;
}

type Tulos = "onnistui" | "ei_onnistunut" | "ei_ymmarretty";
type OmaRivi = { laji: string; tehtava_nro: number | null; tulos: string | null; kysymys: string | null; arvo: number | null; teksti: string | null; muokattu: string };

/** Kellonaika paikallisesti (tallennuskuittaus «Tallennettu 9.42»). */
const klo = (iso: string) => new Date(iso).toLocaleTimeString(lokaali(), { hour: "numeric", minute: "2-digit" });

/** T487: tehtävän tulos tallentuu heti painalluksesta, kommentti kentästä poistuttaessa. */
function TehtavanPalaute({ rooli, nro, teksti, nk, rivi, paivita }: { rooli: string; nro: number; teksti: string; nk: string;
    rivi?: OmaRivi; paivita: () => void }) {
  const [kommentti, setKommentti] = useState(rivi?.teksti ?? "");
  const [virhe, setVirhe] = useState<string | null>(null);
  useEffect(() => { setKommentti(rivi?.teksti ?? ""); }, [rivi?.teksti, nk]);
  const tallenna = async (muutos: { tulos?: Tulos | null; kommentti?: string | null }) => {
    try { await api.tallennaTehtava({ rooli, nro, tehtava: teksti, kieli: kieli(), nimikirjaimet: nk, ...muutos }); setVirhe(null); paivita(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("tpa.tallennus_virhe")); }
  };
  const valinta = (arvo: Tulos, avain: KaannosAvain) => (
    <button type="button" className={"valintanappi valintanappi--" + arvo} aria-pressed={rivi?.tulos === arvo}
            data-tehtava-tulos={arvo} onClick={() => void tallenna({ tulos: rivi?.tulos === arvo ? null : arvo })}>{t(avain)}</button>
  );
  return (
    <div className="testaus__palaute">
      <div className="testaus__valinnat" role="group" aria-label={t("tpa.tehtavan_tulos", { nro })}>
        {valinta("onnistui", "tpa.tulos.onnistui")}
        {valinta("ei_onnistunut", "tpa.tulos.ei_onnistunut")}
        {valinta("ei_ymmarretty", "tpa.tulos.ei_ymmarretty")}
      </div>
      <label className="testaus__kommentti">{t("tpa.kommentti")}
        <input value={kommentti} maxLength={500} onChange={(e) => setKommentti(e.target.value)} data-tehtava-kommentti={nro}
               onBlur={() => { if ((kommentti.trim() || null) !== (rivi?.teksti ?? null)) void tallenna({ kommentti: kommentti.trim() || null }); }} />
      </label>
      {rivi && <span className="testaus__tallennettu" data-tehtava-tallennettu={nro}>{t("tpa.tallennettu", { klo: klo(rivi.muokattu) })}</span>}
      {virhe && <span className="huom huom--vika" role="alert">{virhe}</span>}
    </div>
  );
}

/** T487b: «Uutta testattavaa» — 14 päivän rivit uusimmat ensin, linkki näkymään, «katsottu» kun linkki on avattu. */
function UuttaTestattavaa({ nk }: { nk: string }) {
  const [rivit, setRivit] = useState<{ avain: string; lahetetty: string; linkki: string; rooli: string | null; teksti: string; katsottu: boolean }[] | null>(null);
  useEffect(() => { api.testattavaaUutta(nk).then((o) => setRivit(o.rivit)).catch(() => setRivit([])); }, [nk]);
  if (!rivit || rivit.length === 0) return null;
  return (
    <section className="testaus__uutta vain-naytto" data-testaus-uutta={rivit.length}>
      <h2>{t("tpa.uutta_otsikko")}</h2>
      <ul>
        {rivit.map((r) => (
          <li key={r.avain} data-uutta-avain={r.avain} data-katsottu={r.katsottu ? "1" : "0"}>
            <a href={r.linkki} onClick={() => { void api.merkitseKatsotuksi(r.avain, kieli(), nk).catch(() => {}); }}>{r.teksti}</a>
            {r.rooli && <span className="dim"> · {t(`ohjeet.rooli.${r.rooli}` as KaannosAvain)}</span>}
            {r.katsottu && <span className="testaus__katsottu"> · {t("tpa.katsottu")}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

const LOPPUKYSYMYKSET = ["puuttuu", "vaikea", "hienosti"] as const;

function Loppukysymykset({ rooli, nk, rivit, paivita }: { rooli: string; nk: string; rivit: OmaRivi[]; paivita: () => void }) {
  const rivi = (k: string) => rivit.find((x) => x.laji === "loppu" && x.kysymys === k);
  const [arvot, setArvot] = useState<Record<string, string>>({});
  useEffect(() => {
    const a: Record<string, string> = {};
    for (const k of [...LOPPUKYSYMYKSET, "perustelu"]) a[k] = rivi(k)?.teksti ?? "";
    setArvot(a);
  }, [rivit]); // eslint-disable-line
  const tallenna = async (kysymys: string, muutos: { teksti?: string | null; arvo?: number | null }) => {
    await api.tallennaLoppu({ rooli, kysymys, kieli: kieli(), nimikirjaimet: nk, ...muutos }); paivita();
  };
  const tekstikentta = (k: string, avain: KaannosAvain, rivejä: number) => (
    <label key={k}>{t(avain)}
      {rivejä > 1
        ? <textarea rows={rivejä} value={arvot[k] ?? ""} onChange={(e) => setArvot({ ...arvot, [k]: e.target.value })} data-loppu={k}
                    onBlur={() => { if ((arvot[k] ?? "").trim() !== (rivi(k)?.teksti ?? "")) void tallenna(k, { teksti: (arvot[k] ?? "").trim() || null }); }} />
        : <input value={arvot[k] ?? ""} maxLength={300} onChange={(e) => setArvot({ ...arvot, [k]: e.target.value })} data-loppu={k}
                 placeholder={t("tpa.perustelu_esim")}
                 onBlur={() => { if ((arvot[k] ?? "").trim() !== (rivi(k)?.teksti ?? "")) void tallenna(k, { teksti: (arvot[k] ?? "").trim() || null }); }} />}
    </label>
  );
  const kayttaisi = rivi("kayttaisi")?.arvo ?? null;
  return (
    <section className="testaus__loppu" data-testaus-loppu="">
      <h2>{t("tpa.lopuksi")}</h2>
      {tekstikentta("puuttuu", "tpa.kysymys.puuttuu", 3)}
      {tekstikentta("vaikea", "tpa.kysymys.vaikea", 3)}
      {tekstikentta("hienosti", "tpa.kysymys.hienosti", 3)}
      <fieldset className="testaus__asteikko">
        <legend>{t("tpa.kysymys.kayttaisi")}</legend>
        <div className="testaus__valinnat">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" className="valintanappi" aria-pressed={kayttaisi === n} data-loppu-arvo={n}
                    onClick={() => void tallenna("kayttaisi", { arvo: kayttaisi === n ? null : n })}>{n}</button>
          ))}
        </div>
      </fieldset>
      {tekstikentta("perustelu", "tpa.kysymys.perustelu", 1)}
    </section>
  );
}

export default function Testaus() {
  const [ohje, setOhje] = useState<Awaited<ReturnType<typeof api.testausohje>> | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  // T481 (Toni 11.10.2026): sama roolivalinta kuin Ohjeissa — pääkäyttäjä ja projektipäällikkö valitsevat, muut näkevät omansa.
  const [rooli, setRooli] = useState<string | undefined>(undefined);
  const [nk] = useNimikirjaimet();
  const [omat, setOmat] = useState<OmaRivi[]>([]);
  const haeOmat = useCallback(() => {
    if (!ohje) return;
    api.testauspalauteOma(ohje.rooli, nk).then((o) => setOmat(o.rivit)).catch(() => setOmat([]));
  }, [ohje, nk]);
  useEffect(() => { haeOmat(); }, [haeOmat]);
  useEffect(() => {
    api.testausohje(kieli(), rooli).then((o) => { setOhje(o); setVirhe(null); })
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("testaus.haku_virhe")));
  }, [rooli]);
  return (
    <>
      <header className="head tuloste-tyokalut">
        <div className="eyebrow">{t("testaus.yla")}</div>
        <div className="head__rivi">
          <h1>{t("testaus.otsikko")}</h1>
          <div className="head__ohjaus">
            {ohje?.roolit && (
              <label className="ohjeet__rooli">
                <span className="dim">{t("ohjeet.rooli")}</span>
                <select value={rooli ?? ohje.oma_rooli} onChange={(e) => setRooli(e.target.value)} data-testaus-roolivalinta="">
                  {ohje.roolit.map((r) => <option key={r} value={r}>{t(`ohjeet.rooli.${r}` as KaannosAvain)}</option>)}
                </select>
              </label>
            )}
            <button type="button" className="nappi nappi--kevyt" onClick={() => window.print()} disabled={!ohje} data-testaus-tulosta="">
              {t("testaus.tulosta")}
            </button>
          </div>
        </div>
      </header>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {!ohje && !virhe && <p className="dim">{t("yleinen.ladataan")}</p>}
      {ohje && (
        <article className="panel testausohje tuloste" data-testaus-rooli={ohje.rooli} data-testaus-kieli={ohje.kieli}
                 data-testaus-nakyy={ohje.nakyy.join(",")} data-testaus-ei-nay={ohje.ei_nay.join(",")}>
          {/* Kieliversio puuttuu (11.10.2026): sanotaan lukijan kielellä, ettei kielivaihto näytä rikkinäiseltä. */}
          {ohje.kieli !== kieli() && <p className="huom" data-ohje-kieliversio-puuttuu={kieli()}>{t("ohjeet.vain_kielella", { kieli: t(`kieli.nimi.${ohje.kieli}` as KaannosAvain) })}</p>}
          {ohje.otsikko && <h1>{ohje.otsikko}</h1>}
          <UuttaTestattavaa nk={nk} />
          <div className="testaus__testaaja vain-naytto">
            <NimikirjainKentta />
            <p className="dim">{t("tpa.nimikirjaimet_selite")}</p>
            <p className="dim">{t("tpa.ohje_merkitse")}</p>
          </div>
          <Markdown teksti={ohje.sisalto} tehtava={(nro, teksti) => (
            <TehtavanPalaute key={ohje.rooli + nro + nk} rooli={ohje.rooli} nro={nro} teksti={teksti} nk={nk} paivita={haeOmat}
                             rivi={omat.find((x) => x.laji === "tehtava" && x.tehtava_nro === nro)} />)} />
          <Loppukysymykset rooli={ohje.rooli} nk={nk} rivit={omat} paivita={haeOmat} />
          {/* Tulosteeseen tyhjä Löydökset-taulukko (pääChatin sisältöohje 10.10.); vaiheessa 2 tilalle tulee lomake. */}
          <table className="testaus__loydokset vain-tuloste">
            <caption>{t("testaus.loydokset")}</caption>
            <thead><tr><th>{t("testaus.l_missa")}</th><th>{t("testaus.l_tapahtui")}</th><th>{t("testaus.l_odotin")}</th></tr></thead>
            {/* Viisi riviä; tulosteessa taulukko venyy ensimmäisen sivun loppuun eikä tyhjiä rivejä jatketa toiselle sivulle (T481). */}
            <tbody className="testaus__alku">{Array.from({ length: 5 }, (_, i) => <tr key={i}><td /><td /><td /></tr>)}</tbody>
          </table>
        </article>
      )}
    </>
  );
}
